import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { useTranslation } from '../../i18n';
import { flightDetailsKeys } from './flightDetails.queries';

// STD-015: write/imperative actions for the flight-details domain. apiService is
// imported here (a *.mutations.ts file) per STD-016. Errors are toasted by the
// global MutationCache handler; meta.errorMessage is the fallback.

// Fetch weather for a flight — triggered by the "Fetch Weather" button in
// FlightDetailsDialog. Returns the weather payload on success.
export function useFetchFlightWeatherMutation() {
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('statistics.failedToFetchWeather') },
    mutationFn: async (flightId: string) => {
      const response = await apiService.fetchFlightWeather(flightId);
      return response;
    },
  });
}

// Update flight metadata (pilot, payload, customer, etc.) — triggered by the
// Save button in the edit form inside FlightDetailsDialog.
export function useUpdateFlightMetadataMutation(droneSerial: string, flightDate: string | null) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    meta: { errorMessage: t('statistics.failedToUpdateFlight') },
    mutationFn: async ({
      flightId,
      data,
    }: {
      flightId: string;
      data: {
        pilot_id?: string;
        payload_id?: number;
        customer_id?: string;
        project_id?: string;
        airspace_observer_id?: string;
        payload_operator_id?: string;
        flight_type?: string;
      };
    }) => {
      return apiService.updateFlightMetadata(flightId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: flightDetailsKeys.detail(droneSerial, flightDate),
      });
    },
  });
}
